import { addMonths } from "date-fns"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "design-system/molecules/Chart"
import { ChartCard } from "design-system/organisms/insights/ChartCard"
import { cn } from "design-system/utils"
import { capitalize, formatDate, isNil } from "lopermut"
import { useNPSQuery } from "modules/insights/hooks/useNPSQuery"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Bar, CartesianGrid, ComposedChart, DotProps, Line, XAxis, YAxis } from "recharts"

type Props = {
  data: ReturnType<typeof useNPSQuery>["data"]
  isPending: boolean
}

function generatePromoterEvolutionFakeData() {
  return new Array(10).fill("").map((_, index) => {
    const date = addMonths(new Date(), -index)

    return {
      count: Math.ceil(Math.random() * 100),
      period: formatDate(date, "MMM d"),
      axisPeriod: formatDate(date, "MMM d"),
      promoters: Math.ceil(Math.random() * 100),
      detractors: Math.ceil(Math.random() * 100),
      passives: Math.ceil(Math.random() * 100),
      score: Math.ceil(Math.random() * 100),
    }
  })
}

const MIN_WIDTH_TO_SHOW_WEEK_LABEL = 600

export function NetPromoterEvolution({ data, isPending }: Props) {
  const { t } = useTranslation()
  const noData = !isPending && data?.data.count === 0

  const [showXLabel, setShowXLabel] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleResize = () => {
      setShowXLabel(
        Boolean(
          chartRef.current?.clientWidth &&
          chartRef.current.clientWidth > MIN_WIDTH_TO_SHOW_WEEK_LABEL,
        ),
      )
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const chartData = useMemo(
    () =>
      noData
        ? generatePromoterEvolutionFakeData()
        : data?.data.distribution
            ?.filter((e): e is NonNullable<typeof e> => e != null)
            .map((e) => ({
              ...e,
              period: `${capitalize(formatDate(e.period.from))} - ${capitalize(formatDate(e.period.to))}`,
              axisPeriod: capitalize(formatDate(e.period.from, "MMM d")),
            })),
    [data, noData],
  )

  const chartConfig = {
    score: {
      label: t("insights.nps.evolution.score"),
    },
    count: {
      label: t("insights.nps.evolution.count"),
    },
  } satisfies ChartConfig

  const chart = (
    <ChartContainer
      config={chartConfig}
      className={cn(
        "mx-auto w-full h-full",
        noData && "pointer-events-none grayscale opacity-12 dark:opacity-8",
      )}
      ref={chartRef}
    >
      <ComposedChart
        accessibilityLayer
        data={chartData}
        maxBarSize={50}
        margin={{
          top: 30,
          left: 20,
          right: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid vertical={false} yAxisId="totals" />
        <YAxis yAxisId="totals" tickLine={false} axisLine={false} tickMargin={10} width={30} />
        <YAxis
          yAxisId="score"
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          width={30}
          domain={[-100, 100]}
          hide={true}
        />
        <XAxis
          dataKey="period"
          tickLine={false}
          tickMargin={10}
          axisLine={false}
          interval={0}
          height={40}
          hide={!showXLabel}
          tickFormatter={(_, index) => chartData?.[index]?.axisPeriod ?? ""}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar yAxisId="totals" dataKey="count" fill="var(--chart-secondary)" />
        <Line
          yAxisId="score"
          dataKey="score"
          type="natural"
          stroke="var(--chart-primary)"
          strokeWidth={3}
          activeDot={(props: DotProps) => {
            const { cx, cy } = props
            if (!cx || !cy) return <span />

            return (
              <circle
                key={cx}
                cx={cx}
                cy={cy}
                r={5}
                fill="var(--background)"
                stroke="var(--chart-primary)"
                strokeWidth={2}
              />
            )
          }}
          dot={(props) => {
            const { payload, cx, cy } = props
            const score = payload.score

            if (isNil(score) || !cx || !cy) return <span />

            return (
              <circle
                key={cx}
                cx={cx}
                cy={cy}
                r={6}
                fill="var(--chart-primary)"
                stroke="var(--chart-primary)"
                strokeWidth={2}
              />
            )
          }}
          label={{
            position: "top",
            fill: "var(--foreground)",
            offset: 12,
            fontSize: 12,
            opacity: noData ? 0 : 1,
          }}
        />
      </ComposedChart>
    </ChartContainer>
  )

  return (
    <ChartCard
      title={t("insights.nps.evolution.title")}
      description={t("insights.nps.evolution.description")}
      chart={chart}
      isPending={isPending}
      noData={{
        show: noData,
        title: t("insights.nps.noData.title"),
        description: t("insights.nps.noData.description"),
      }}
    />
  )
}
